import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Newspaper, Calendar, Eye, ArrowRight, Sparkles, TrendingUp, Megaphone, Leaf } from "lucide-react";
import ProposeNewsModal from "./ProposeNewsModal";
import { Badge, cover, fmtDate, EventsBlock, CommsBlock } from "./HomeBlocks";
import NewsModal from "./NewsModal";

const FILTERS = ["Tous", "Groupe", "Réseau", "Commerce", "RH", "Marketing", "Qualité", "Info", "Vie d'entreprise"];

export default function IntranetNews({ go }) {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [comms, setComms] = useState([]);
  const [filter, setFilter] = useState("Tous");
  const [open, setOpen] = useState(null);
  const [propose, setPropose] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get("/intranet/news", { params: { limit: 100 } }).then(r => setNews(r.data || [])).catch(() => {});
    api.get("/intranet/event", { params: { limit: 4 } }).then(r => setEvents(r.data || [])).catch(() => {});
    api.get("/intranet/comm", { params: { limit: 4 } }).then(r => setComms(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => filter === "Tous" ? news : news.filter(n => n.category === filter), [news, filter]);
  const [featured, ...rest] = filtered;
  const latest = showAll ? rest : rest.slice(0, 6);
  const mostViewed = useMemo(() => [...news].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5), [news]);

  const show = async (n) => {
    setOpen(n);
    try {
      const { data } = await api.post(`/intranet-news-view/${n.id}`);
      setNews(list => list.map(x => x.id === n.id ? { ...x, views: data.views } : x));
      setOpen(x => x && { ...x, views: data.views });
    } catch {}
  };

  return (
    <div className="space-y-6" data-testid="intranet-news-page">
      <section className="relative rounded-lg overflow-hidden text-white min-h-[260px]" data-testid="news-hero">
        <img src="/intranet/news-1.jpg" alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(11,27,58,0.94) 0%, rgba(11,27,58,0.85) 45%, rgba(11,27,58,0.35) 100%)" }}/>
        <div className="relative px-6 sm:px-10 py-10 flex items-center justify-between gap-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-[11px] font-bold uppercase tracking-widest" data-testid="news-hero-badge"><Newspaper className="w-3.5 h-3.5"/>Actualités</span>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">Toute la vie d'Orbit <span className="text-[#60a5fa]">en un seul endroit</span></h1>
            <p className="mt-3 text-sm sm:text-base text-slate-200 max-w-xl">Découvrez les dernières actualités, les projets, les succès et les informations importantes du Groupe</p>
          </div>
          <div className="hidden lg:flex flex-col items-center gap-3 shrink-0">
            <div className="-rotate-3 bg-white/95 text-[#0b1b3a] px-6 py-4 rounded-sm shadow-2xl border-4 border-white" data-testid="news-hero-slogan">
              <div className="font-black text-2xl leading-none" style={{ letterSpacing: "-0.03em" }}>Good Food</div>
              <div className="text-[#f97316] italic text-2xl leading-none mt-1" style={{ fontFamily: "Georgia, serif", fontWeight: 700 }}>Good People</div>
            </div>
            <img src="/intranet/roy.png" alt="" className="h-20 object-contain drop-shadow-xl"/>
          </div>
        </div>
      </section>

      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="news-filters">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`news-filter-${f.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
                  className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === f ? "bg-[#0b1b3a] text-white border-[#0b1b3a]" : "bg-white text-slate-600 border-slate-200 hover:border-[#1d4ed8] hover:text-[#1d4ed8]"}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {featured ? (
            <article className="bg-white border border-slate-200 rounded-lg overflow-hidden grid grid-cols-1 md:grid-cols-2" data-testid={`news-featured-${featured.id}`}>
              <div className="aspect-[4/3] md:aspect-auto md:min-h-[300px] bg-slate-100">
                {cover(featured.cover_url) && <img src={cover(featured.cover_url)} alt="" className="w-full h-full object-cover"/>}
              </div>
              <div className="p-6 flex flex-col">
                {featured.category && <Badge label={featured.category}/>}
                <h2 className="mt-3 text-xl lg:text-2xl font-bold text-[#0b1b3a] leading-snug">{featured.title}</h2>
                {featured.summary && <p className="mt-2 text-sm text-slate-600 line-clamp-4">{featured.summary}</p>}
                <div className="mt-auto pt-4 flex items-center gap-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5"/>{fmtDate(featured.created_at)}</span>
                  <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5"/>{featured.views || 0}</span>
                </div>
                <button onClick={() => show(featured)} data-testid="news-featured-read" className="mt-4 self-start px-4 py-2 rounded-lg bg-[#1d4ed8] hover:bg-blue-800 text-white text-sm font-semibold flex items-center gap-2 transition-colors">
                  Lire la suite <ArrowRight className="w-4 h-4"/>
                </button>
              </div>
            </article>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-sm text-slate-400" data-testid="news-empty">Aucune actualité dans cette catégorie.</div>
          )}

        </div>

        <aside className="space-y-4 self-start" data-testid="news-sidebar">
          <CommsBlock comms={comms}/>
          <EventsBlock events={events} onAll={() => go("vie")}/>
          <div className="rounded-lg p-5 text-white" style={{ background: "#0b1b3a" }}>
            <Sparkles className="w-5 h-5 text-[#60a5fa]"/>
            <div className="mt-2 text-sm font-semibold">Une info à partager ?</div>
            <p className="mt-1 text-xs text-slate-300">Publiez-la sur WasaGram ou contactez la communication interne.</p>
            <button onClick={() => go("wasagram")} data-testid="news-cta-wasagram" className="mt-3 text-xs font-bold text-[#60a5fa] hover:underline flex items-center gap-1">Ouvrir WasaGram <ArrowRight className="w-3.5 h-3.5"/></button>
          </div>
        </aside>
      </div>

      <section data-testid="news-latest">
        <div className="flex items-center justify-between mb-3">
          <h2 className="flex items-center gap-2 text-base md:text-lg font-bold text-[#0b1b3a]"><Newspaper className="w-5 h-5 text-[#1d4ed8]"/>Dernières actualités</h2>
          {rest.length > 6 && <button onClick={() => setShowAll(v => !v)} data-testid="news-latest-all" className="text-xs font-semibold text-[#1d4ed8] hover:underline flex items-center gap-1">{showAll ? "Réduire" : "Voir toutes les actualités"} <ArrowRight className="w-3.5 h-3.5"/></button>}
        </div>
        {latest.length === 0 ? <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-sm text-slate-400">Pas d'autre actualité dans cette catégorie.</div> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="news-grid">
            {latest.map(n => (
              <button key={n.id} onClick={() => show(n)} data-testid={`news-card-${n.id}`} className="group text-left bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                  {cover(n.cover_url) && <img src={cover(n.cover_url)} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"/>}
                </div>
                <div className="p-4">
                  {n.category && <Badge label={n.category}/>}
                  <div className="mt-2 text-sm font-bold text-[#0b1b3a] leading-snug line-clamp-2">{n.title}</div>
                  {n.summary && <div className="mt-1 text-xs text-slate-500 line-clamp-2">{n.summary}</div>}
                  <div className="mt-2 text-[11px] text-slate-400 flex items-center gap-3">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3"/>{fmtDate(n.created_at)}</span>
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3"/>{n.views || 0}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="bg-white border border-slate-200 rounded-lg p-4" data-testid="news-most-viewed">
          <div className="flex items-center justify-between mb-2">
            <h2 className="flex items-center gap-2 text-base font-bold text-[#0b1b3a]"><TrendingUp className="w-5 h-5 text-[#1d4ed8]"/>Les plus consultées</h2>
            <button onClick={() => { setFilter("Tous"); setShowAll(true); }} data-testid="news-most-viewed-all" className="text-xs font-semibold text-[#1d4ed8] hover:underline flex items-center gap-1">Voir tout <ArrowRight className="w-3.5 h-3.5"/></button>
          </div>
          <ol className="divide-y divide-slate-100">
            {mostViewed.map((n, i) => (
              <li key={n.id}>
                <button onClick={() => show(n)} data-testid={`news-top-${i + 1}`} className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-slate-50 rounded-md px-1">
                  <span className={`w-7 h-7 rounded-md grid place-items-center text-sm font-black shrink-0 ${i === 0 ? "bg-[#0b1b3a] text-white" : "bg-[#e0e9ff] text-[#1d4ed8]"}`}>{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#0b1b3a] truncate">{n.title}</div>
                    {n.category && <Badge label={n.category} className="mt-0.5"/>}
                  </div>
                  <span className="text-xs text-slate-400 flex items-center gap-1 shrink-0"><Eye className="w-3.5 h-3.5"/>{n.views || 0}</span>
                </button>
              </li>
            ))}
          </ol>
        </section>

        <section className="lg:col-span-2 relative overflow-hidden rounded-lg border border-[#c7d7fe] p-6 sm:p-8 flex items-center justify-between gap-6" style={{ background: "linear-gradient(135deg,#e0f2fe 0%,#dcfce7 100%)" }} data-testid="news-propose-cta">
          <Leaf className="absolute -left-6 -bottom-8 w-40 h-40 text-emerald-600/10 rotate-12"/>
          <div className="relative">
            <div className="text-xl sm:text-2xl font-bold text-[#0b1b3a]">Une actualité à partager ?</div>
            <p className="mt-1 text-sm text-slate-600">Proposez une actualité au service communication</p>
            <button onClick={() => setPropose(true)} data-testid="news-propose-btn" className="mt-4 px-5 py-2.5 rounded-lg bg-[#1d4ed8] hover:bg-blue-800 text-white text-sm font-semibold flex items-center gap-2 transition-colors">
              Proposer une actualité <ArrowRight className="w-4 h-4"/>
            </button>
          </div>
          <img src="/intranet/roy.png" alt="" className="relative h-28 sm:h-36 object-contain shrink-0 drop-shadow-lg"/>
        </section>
      </div>

      <section className="rounded-lg text-white px-6 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ background: "#0b1b3a" }} data-testid="news-wasagram-cta">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-white/10 grid place-items-center shrink-0"><Megaphone className="w-6 h-6 text-[#60a5fa]"/></div>
          <div>
            <div className="text-lg font-bold">Sur Orbit, c'est aussi WasaGram !</div>
            <p className="text-sm text-slate-300">Partagez le quotidien de votre réseau, vos idées et vos réussites</p>
          </div>
        </div>
        <button onClick={() => go("wasagram")} data-testid="news-wasagram-btn" className="px-5 py-2.5 rounded-lg bg-[#1d4ed8] hover:bg-blue-800 text-white text-sm font-semibold flex items-center gap-2 transition-colors shrink-0">
          Accéder à WasaGram <ArrowRight className="w-4 h-4"/>
        </button>
      </section>

      <ProposeNewsModal open={propose} onClose={() => setPropose(false)}/>
      <NewsModal news={open} onClose={() => setOpen(null)}/>
    </div>
  );
}
